import {
  realTimerEnvironment,
  RunningTimeTimeoutHandle,
  scheduleRunningTimeTimeout,
  TimerEnvironment,
} from './running-clock';

export enum DocChangeType {
  Changed = 'changed',
  New = 'new',
}

export interface DocChange {
  id: string;
  type: DocChangeType;
}

/**
 * A watcher callback. It MAY return a promise — and it should, whenever it
 * starts asynchronous work. Returning the promise is what lets the watcher
 * keep two runs from overlapping (see the class comment below). Writing
 * `void this.somethingAsync()` instead hides the work from the watcher and
 * brings back the pile-up this class exists to prevent.
 *
 * A callback MUST tolerate being handed the same id more than once. After a
 * failed or abandoned delivery the watcher puts that batch's ids back and
 * delivers them again later, so an id can arrive twice. Every callback today
 * re-derives its state from the database, which makes a repeat harmless — do
 * the same rather than treating a delivery as a one-off event to act on.
 */
export type DocWatcherCallback = (changes: DocChange[]) => void | Promise<void>;

/**
 * The clock and timers the watcher needs, passed in rather than taken from the
 * global scope so unit tests can drive time by hand (this project has no
 * fake-timer library). Production callers leave this out and get the real ones.
 *
 * This is `TimerEnvironment` from `running-clock.ts`, not a shape of its own:
 * the stuck watchdog below is a running-time limit from that module, and it
 * takes exactly this. `nowMs` must be a MONOTONIC clock (`performance.now()`),
 * never the wall clock, for the reason that module's own comment gives.
 */
export type DocWatcherTimers = TimerEnvironment;

/** How long the database must stay quiet before a batch of notifications is
 *  delivered. Small enough that a single change synced from another device
 *  still appears to arrive at once; large enough to collapse a sync burst. */
export const DOC_WATCHER_DEBOUNCE_MS = 200;

/** The longest a watcher will hold notifications back when the quiet period
 *  above never arrives. Load-bearing — see the class comment. */
export const DOC_WATCHER_MAX_WAIT_MS = 3_000;

/** How long one callback may stay in flight before we report it. Far longer
 *  than any legitimate page reload, short enough to catch a real hang within
 *  a single session.
 *
 *  This is a budget of RUNNING time, not of wall-clock time. See
 *  `armStuckCallbackReport` for why, and `design-docs/reactive-sync.md`
 *  point 3. */
export const DOC_WATCHER_STUCK_CALLBACK_MS = 30_000;

/** How many times in a row the watcher re-delivers a failed batch on its own
 *  before it stops trying and waits for the next notification instead.
 *
 *  The exact value is not load-bearing. A retry goes out one quiet period
 *  later, so even a handful of them cover well under a second — far less than
 *  any real transient fault lasts. What fixes the staleness is that the ids are
 *  kept rather than dropped; the retries only catch a fault that clears
 *  instantly. So this number just has to be small enough not to spin. */
export const DOC_WATCHER_MAX_DELIVERY_RETRIES = 2;

export interface DocWatcherOptions {
  /** Names this watcher in the stuck-callback report. Worth setting on any
   *  watcher whose callback does real work; pointless on a throwaway one. */
  label?: string;
  debounceMs?: number;
  maxWaitMs?: number;
  stuckCallbackMs?: number;
  maxDeliveryRetries?: number;
  timers?: DocWatcherTimers;
  /** Called when the callback throws, or its promise rejects. The watcher
   *  cannot decide what a failure means, so it hands it back rather than
   *  swallowing it. */
  onCallbackError?: (err: unknown, label: string) => void;
  /** Called once per run if the callback is still in flight after
   *  `stuckCallbackMs` of RUNNING time. Without this, single-flight could
   *  silence a page forever with nothing to debug from.
   *
   *  `hiddenMs` is the time the limit set aside as "the page was not running",
   *  measured by the running clock. It is what lets a reader of one report tell
   *  a run that genuinely hung from one that merely spanned a freeze — which
   *  nothing in a report could do before (df-651b2616e742). */
  onCallbackStuck?: (info: { label: string; pendingSample: string[]; stuckMs: number; hiddenMs: number }) => void;
  /** Called once when a batch has failed `maxDeliveryRetries` times in a row
   *  and the watcher stops re-delivering it on its own. The ids are kept, so
   *  this is not a lost batch — it says the page will not catch up until
   *  something else notifies this watcher, which is worth knowing. */
  onRetriesExhausted?: (info: { label: string; pendingSample: string[]; failures: number }) => void;
}


/** True for anything we can await. Deliberately duck-typed: a callback may
 *  return a real Promise, a Promise-like from another realm, or nothing. */
function isPromise(value: void | Promise<void>): value is Promise<void> {
  return !!value && typeof (value as Promise<void>).then === 'function';
}

/**
 * Reactive sync: notifies UI components when their watched CouchDB documents change via cross-device sync.
 *
 * How it works:
 * 1. A component creates a DocWatcher via `database.createDocWatcher(callback)`
 * 2. It passes the watcher to database getters (loadPlanAsync, getOneLogEntryAsync, etc.)
 * 3. The getter calls `registerDocWithWatcher(docId, watcher)` — linking the doc to this watcher
 * 4. When PouchDB sync pulls a change, DatabaseWrapper calls `notifyDocWatchers(docId)`
 * 5. The watcher collects notifications and delivers them as one batch (below)
 * 6. The callback re-derives UI state and calls markForCheck()
 *
 * Two notification types:
 * - `Changed`: a doc this watcher was tracking got updated (targeted to specific watchers)
 * - `New`: an untracked doc arrived via sync — broadcast to ALL active watchers
 *
 * This replaces the old full-page-reload approach (`location.reload()` on cross-device sync).
 * Reactive sync is now always-on — there is no feature flag.
 *
 * ## When the callback actually runs (#4358)
 *
 * The `New` broadcast above is the thing to understand. Any pulled document
 * that no watcher happens to be tracking wakes EVERY open page. So one large
 * sync — most sharply a coach entering manage mode, which pulls a whole client
 * database — wakes every page once per replication batch, thousands of times.
 * A page that responded to each wake-up by starting another reload without
 * waiting for the previous one piled up dozens of overlapping runs and froze
 * the single JS thread. That was #4243 on the Training page and #4358 on Home.
 *
 * Rather than expect every page to remember a guard, the watcher enforces
 * these things for all of them:
 *
 * 1. **Single-flight.** A callback that returns a promise is not invoked again
 *    until that promise settles. Notifications arriving meanwhile accumulate
 *    and go out as ONE later call carrying every id that piled up — held back,
 *    never dropped. This only works if the callback RETURNS its promise;
 *    `void this.somethingAsync()` hides the work and reopens the hole.
 * 2. **A quiet period, with a ceiling.** A batch goes out once the database
 *    has been quiet for `debounceMs`. If notifications never stop, it goes out
 *    anyway every `maxWaitMs`. The ceiling is load-bearing: without it this
 *    would be a plain debounce, which during a sync lasting tens of seconds
 *    delivers NOTHING — stalling the intake form's cross-device navigation
 *    branch (#4059) and `waitForDocChangeAsync`.
 * 3. **A stuck callback is reported AND the guard is released.** Single-flight
 *    means a promise that never settles would silence a page forever, so that
 *    case surfaces instead of going quiet — and the guard is then released so
 *    the page recovers. Holding it would trade a slow page for a permanently
 *    deaf one, which is worse than the pile-up this class prevents.
 *    **The 30 seconds are RUNNING time**, counted by a running-time limit from
 *    `running-clock.ts`, because a run the platform froze is not a run that
 *    hung. `armStuckCallbackReport` below carries that reasoning and the
 *    production evidence for it.
 * 4. **A failed delivery keeps its ids.** The batch is drained out of the
 *    pending sets before the callback runs, so a callback that fails used to
 *    take those ids with it and the page could stay stale for good — a
 *    wake-up-only callback recovered on the next notification, but one that
 *    checks the ids (`changes.some(c => c.id === …)`) never saw them again.
 *    Now the ids go back, and the retrying — not the ids — is what is bounded.
 *    `requeueFailedBatch` below carries the rule; `design-docs/reactive-sync.md`
 *    point 5 carries the reasoning, including why an abandoned stuck run has to
 *    count as a failure.
 *
 * Worst-case staleness is therefore `maxWaitMs` plus one run's own duration —
 * the ceiling is not armed while a run is in flight, because the run's
 * completion is what re-arms it.
 */
export class DocWatcher {
  private watchedDocIds = new Set<string>();
  private pendingChanges = new Set<string>();
  private pendingNewDocs = new Set<string>();
  private destroyed = false;

  /** Timer for the quiet period. Restarted by every new notification. */
  private quietTimer: unknown = null;
  /** Timer for the maximum wait. Started once per batch and NEVER restarted —
   *  that is what stops a continuous storm from postponing delivery forever. */
  private ceilingTimer: unknown = null;
  /** The running-time limit that reports a callback which never settles. It also
   *  carries, in `notRunningMs()`, how long this run was frozen. */
  private stuckWatchdog: RunningTimeTimeoutHandle | null = null;
  /** True while a callback returned a promise that has not settled yet. */
  private running = false;
  /** Identifies the run that currently owns `running`. Bumped when a run
   *  starts, and again when the stuck watchdog gives up on one — so a run that
   *  settles after being abandoned can tell that it no longer owns the guard. */
  private currentRunToken = 0;
  /** How many deliveries have failed in a row without one succeeding in
   *  between. Reset to 0 by any successful delivery. */
  private consecutiveFailures = 0;

  private readonly label: string;
  private readonly debounceMs: number;
  private readonly maxWaitMs: number;
  private readonly stuckCallbackMs: number;
  private readonly maxDeliveryRetries: number;
  private readonly timers: DocWatcherTimers;
  private readonly onCallbackError?: (err: unknown, label: string) => void;
  private readonly onCallbackStuck?: (info: { label: string; pendingSample: string[]; stuckMs: number; hiddenMs: number }) => void;
  private readonly onRetriesExhausted?: (info: { label: string; pendingSample: string[]; failures: number }) => void;

  constructor(
    private callback: DocWatcherCallback,
    private deregisterFn: (watcher: DocWatcher, docIds: Set<string>) => void,
    options: DocWatcherOptions = {},
  ) {
    this.label = options.label ?? 'unlabelled';
    this.debounceMs = options.debounceMs ?? DOC_WATCHER_DEBOUNCE_MS;
    this.maxWaitMs = options.maxWaitMs ?? DOC_WATCHER_MAX_WAIT_MS;
    this.stuckCallbackMs = options.stuckCallbackMs ?? DOC_WATCHER_STUCK_CALLBACK_MS;
    this.maxDeliveryRetries = options.maxDeliveryRetries ?? DOC_WATCHER_MAX_DELIVERY_RETRIES;
    this.timers = options.timers ?? realTimerEnvironment();
    this.onCallbackError = options.onCallbackError;
    this.onCallbackStuck = options.onCallbackStuck;
    this.onRetriesExhausted = options.onRetriesExhausted;
  }

  addDocId(docId: string): void {
    this.watchedDocIds.add(docId);
  }

  /**
   * Called by DatabaseWrapper when a watched doc is updated by sync.
   */
  notify(docId: string): void {
    if (this.destroyed) return;
    this.pendingChanges.add(docId);
    this.scheduleDelivery();
  }

  /**
   * Called by DatabaseWrapper when an untracked doc arrives via sync.
   * Broadcast to all active watchers so they can decide whether to react.
   */
  notifyNew(docId: string): void {
    if (this.destroyed) return;
    this.pendingNewDocs.add(docId);
    this.scheduleDelivery();
  }

  /**
   * Arm the two timers that decide when the pending batch goes out.
   *
   * While a callback is in flight we arm nothing at all: the run's own
   * completion re-arms them. That is what keeps runs from overlapping, and it
   * is also why the effective worst case is "the maximum wait PLUS however
   * long a run takes" rather than the maximum wait alone.
   */
  private scheduleDelivery(): void {
    if (this.destroyed || this.running) return;

    // quiet period: restart on every notification, so a burst collapses
    if (this.quietTimer !== null) this.timers.clearTimeout(this.quietTimer);
    this.quietTimer = this.timers.setTimeout(() => this.deliverNow(), this.debounceMs);

    // ceiling: start once per batch and leave it alone. Restarting it here
    // would turn this back into a plain debounce, which delivers NOTHING for
    // as long as the storm lasts.
    if (this.ceilingTimer === null) {
      this.ceilingTimer = this.timers.setTimeout(() => this.deliverNow(), this.maxWaitMs);
    }
  }

  private clearDeliveryTimers(): void {
    if (this.quietTimer !== null) {
      this.timers.clearTimeout(this.quietTimer);
      this.quietTimer = null;
    }
    if (this.ceilingTimer !== null) {
      this.timers.clearTimeout(this.ceilingTimer);
      this.ceilingTimer = null;
    }
  }

  /** Hand the accumulated batch to the callback. */
  private deliverNow(): void {
    this.clearDeliveryTimers();
    if (this.destroyed || this.running) return;
    if (this.pendingChanges.size === 0 && this.pendingNewDocs.size === 0) return;

    const changes: DocChange[] = [];
    for (const id of this.pendingChanges) {
      changes.push({ id, type: DocChangeType.Changed });
    }
    for (const id of this.pendingNewDocs) {
      changes.push({ id, type: DocChangeType.New });
    }
    this.pendingChanges.clear();
    this.pendingNewDocs.clear();

    let result: void | Promise<void>;
    this.running = true;
    try {
      // hand over a COPY. We keep `changes` to re-queue on failure, so a
      // callback that sorted this array or drained it with shift()/splice()
      // would change which ids come back — and an emptied one would put
      // nothing back while still counting as a failed attempt, losing the
      // batch silently. Copying costs one array per delivery, and deliveries
      // are already rate-limited by the quiet period.
      result = this.callback(changes.slice());
    } catch (err) {
      // a callback that throws synchronously never became a promise, so finish
      // here rather than waiting for one
      this.running = false;
      this.reportCallbackError(err);
      this.requeueFailedBatch(changes);
      return;
    }

    // a callback that returned nothing has already finished all the work it
    // is going to do, so there is nothing to wait for
    if (!isPromise(result)) {
      this.running = false;
      this.consecutiveFailures = 0;
      this.scheduleIfMoreArrived();
      return;
    }

    // this run owns the guard until it settles. The token is what lets a run
    // that the watchdog already gave up on be ignored if it settles later —
    // by then a newer run may be in flight, and finishing it would clear the
    // NEW run's guard.
    const runToken = ++this.currentRunToken;
    this.armStuckCallbackReport(runToken, changes);
    void result.then(
      () => this.finishRun(runToken, false, undefined, changes),
      (err) => this.finishRun(runToken, true, err, changes),
    );
  }

  /**
   * End a run. `failed` is carried separately from `err` on purpose: a promise
   * can reject with `undefined` (`Promise.reject()`, or `throw undefined` in an
   * async callback), so the error VALUE cannot tell success from failure. Using
   * it as the test would silently swallow exactly that rejection.
   */
  private finishRun(runToken: number, failed: boolean, err: unknown, changes: DocChange[]): void {
    // ignore a run the watchdog already released the guard for — a newer run
    // may own it now, and clearing it here would let two runs overlap.
    // Deliberately no re-queue here either: the watchdog already put this
    // batch back when it gave up, so doing it again would re-add ids a later
    // run has since delivered.
    if (runToken !== this.currentRunToken) {
      if (failed) this.reportCallbackError(err);
      return;
    }
    this.running = false;
    // this run is over, so its watchdog goes with it
    this.clearStuckWatchdog();
    if (failed) {
      this.reportCallbackError(err);
      this.requeueFailedBatch(changes);
      return;
    }
    this.consecutiveFailures = 0;
    this.scheduleIfMoreArrived();
  }

  /** After a run ends, start the clock again if notifications piled up while
   *  it was busy. They go through the same quiet period, so a page cannot end
   *  up running back-to-back for the whole length of a sync. */
  private scheduleIfMoreArrived(): void {
    if (this.destroyed) return;
    if (this.pendingChanges.size === 0 && this.pendingNewDocs.size === 0) return;
    this.scheduleDelivery();
  }

  /**
   * Single-flight means a promise that never settles would stop this watcher
   * delivering anything, ever, with no trace. So when a run overruns
   * `stuckCallbackMs`, do two things: report it, and RELEASE the guard.
   *
   * Releasing matters as much as reporting. Before single-flight existed a hung
   * reload did not stop later ones — they simply overlapped — so holding the
   * guard forever would trade a slow page for a permanently deaf one, which is
   * worse. Releasing bounds the damage instead: the page recovers, and the
   * pile-up this class prevents can still only grow by one run per
   * `stuckCallbackMs`, which is far too slow to saturate anything.
   *
   * The original promise may still settle afterwards. `finishRun` checks the
   * run token and ignores it, so it cannot clear a newer run's guard.
   *
   * **The budget is RUNNING time, not wall-clock time (df-651b2616e742)**, so a
   * phone that froze the app mid-run does not wake to a false report. That is
   * `scheduleRunningTimeTimeout`'s doing, not this function's: its own header in
   * `running-clock.ts` is the authority on how it counts and what it accepts,
   * and `design-docs/reactive-sync.md` point 3 has the production evidence for
   * this watchdog.
   *
   * if the watcher was destroyed while the callback ran, arm nothing — a
   * self-destroying callback is a real shape here (`waitForDocChangeAsync`
   * destroys its own watcher from inside the callback), and `destroy()` has
   * already cleared the timers by this point, so a timer armed now would
   * never be cleared.
   */
  private armStuckCallbackReport(runToken: number, changes: DocChange[]): void {
    if (this.destroyed) return;
    const onCallbackStuck = this.onCallbackStuck;
    if (!onCallbackStuck) return;

    const watchdog = scheduleRunningTimeTimeout(() => {
      this.stuckWatchdog = null;
      if (this.destroyed) return;
      const pendingSample = [
        ...Array.from(this.pendingChanges),
        ...Array.from(this.pendingNewDocs),
      ].slice(0, 5);
      onCallbackStuck({
        label: this.label,
        pendingSample,
        stuckMs: this.stuckCallbackMs,
        hiddenMs: Math.round(watchdog.notRunningMs()),
      });
      // release the guard so this page starts updating again. Bumping the token
      // first is what makes the abandoned run's later settle a no-op.
      if (runToken === this.currentRunToken) {
        this.currentRunToken++;
        this.running = false;
        // this batch is abandoned, not delivered, so it goes back like any
        // other failure — and counting it is what stops one hung callback
        // starting a fresh run every `stuckCallbackMs` for ever
        this.requeueFailedBatch(changes);
      }
    }, this.stuckCallbackMs, this.timers);
    this.stuckWatchdog = watchdog;
  }

  /** Give up the watchdog. Called when the run it belongs to ends, when the
   *  watchdog gives up on that run, and from `destroy()`. */
  private clearStuckWatchdog(): void {
    if (this.stuckWatchdog !== null) {
      this.stuckWatchdog.cancel();
      this.stuckWatchdog = null;
    }
  }

  /**
   * A delivery failed or was abandoned: put its ids back, and decide whether to
   * re-deliver on our own.
   *
   * Past `maxDeliveryRetries` consecutive failures we deliberately do NOT
   * re-arm. The ids stay pending, so the next `notify()` / `notifyNew()` picks
   * them up — an external notification is the only thing that starts the clock
   * again. That is what makes a spin impossible: re-arming here would deliver
   * the same batch to the same failing callback one quiet period later, fail,
   * and come straight back.
   *
   * The cost is that ids which arrived DURING the failed run also wait for that
   * next notification rather than going out immediately. That is the same
   * trade, and it only applies to a watcher whose callback has just failed
   * several times in a row.
   */
  private requeueFailedBatch(changes: DocChange[]): void {
    if (this.destroyed) return;

    for (const change of changes) {
      if (change.type === DocChangeType.Changed) {
        this.pendingChanges.add(change.id);
      } else {
        this.pendingNewDocs.add(change.id);
      }
    }

    this.consecutiveFailures++;
    if (this.consecutiveFailures > this.maxDeliveryRetries) {
      // report on the transition only. Every failure after this one is driven
      // by an external notification and already surfaces through
      // `onCallbackError`, so repeating this would say nothing new.
      if (this.consecutiveFailures === this.maxDeliveryRetries + 1 && this.onRetriesExhausted) {
        const pendingSample = [
          ...Array.from(this.pendingChanges),
          ...Array.from(this.pendingNewDocs),
        ].slice(0, 5);
        this.onRetriesExhausted({
          label: this.label,
          pendingSample,
          failures: this.consecutiveFailures,
        });
      }
      return;
    }

    this.scheduleIfMoreArrived();
  }

  private reportCallbackError(err: unknown): void {
    if (this.onCallbackError) {
      this.onCallbackError(err, this.label);
      return;
    }
    // no reporter wired up (a throwaway watcher) — still must not vanish
    console.error(`DocWatcher[${this.label}]: callback failed`, err);
  }

  /** Deregisters from the registry and clears all state. Call in ngOnDestroy. */
  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.clearDeliveryTimers();
    // a page destroyed while its callback was still in flight would otherwise
    // leave the watchdog's repeating tick running for the life of the app
    this.clearStuckWatchdog();
    this.pendingChanges.clear();
    this.pendingNewDocs.clear();
    this.deregisterFn(this, this.watchedDocIds);
    this.watchedDocIds.clear();
  }
}
